import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../hooks/useContriesData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../config/config";
import { slugify } from "../../utils/utils";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer} from "deck.gl";
import MapControls from "../common/map/MapControls";
import MapToolTip from "../common/mapTooltip/MapToolTip";
import styles from "./map-cities.module.scss";
import PopUpDetail from "../common/popup/PopUpDetail";
import ListItem from "../common/list/ListItem";
import Accordion from "../common/accordion/Accordion";
import { dataCountryOverview } from "../../utils/convert";

const MapCities = ({type}) => {
  const [hoveredCity, setHoveredCity] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [allCitiesData, setAllCitiesData] = useState(null);
  const [dataGroup, setDataGroup] = useState([]);
  const [showId, setShowId] = useState();
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [detailCard, setDetailCard] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  let mapRef = useRef(null);
  let listRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);

  const countriesData = useCountriesData();
  const popUpRef = useRef(null)

  useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );


  useEffect(() => {

    const data = dataCountryOverview();

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO3)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO3)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }

    const groupBy = prop => data => {
        return data.reduce((dict, item) => {
          const { [prop]: _, ...rest } = item;
          dict[item[prop]] = [...(dict[item[prop]] || []), rest];
          return dict;
        }, {});
    };

    if(data && countriesData){

        resetZoom();
        setShowCard(false);
        setSelectedCity(null);

        const filteredTop10 = data.filter((item) =>
            item.top10 === true
        ).sort((a, b)=> 
            b.millionaires - a.millionaires
        );


        const filteredTopByRegion = data.filter((item) =>
            item.topByRegion === true
        ).sort((a, b)=>  
            b.millionaires - a.millionaires
        );

        const dataByRegion = Object.entries(groupBy('region')(filteredTopByRegion))
                                    .map(([key, value]) => ({ name: key, cities: value }));
        const sortedDataByRegion = dataByRegion.sort((a, b)=> {return a.name.localeCompare(b.name);});

        const includeTop10Countries = filteredTop10.map((d) => d.ISO3);
        const includeTopByRegion = filteredTopByRegion.map((d) => d.ISO3);

        let features = countriesData.features; 

        setDataGroup(sortedDataByRegion);

        if(type === 'top10'){
            setAllCitiesData(filteredTop10);
            showVisibleCountries(includeTop10Countries, features);
            hideNotVisibleCountries(includeTop10Countries, features);
           
        }else if(type === 'topByRegion'){
            setAllCitiesData(filteredTopByRegion);
            showVisibleCountries(includeTopByRegion, features);
            hideNotVisibleCountries(includeTopByRegion, features);
        }
        
    }
  }, [countriesData, type, resetZoom]);

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };

  // const handleRadioboxChange = (event) =>{
  //   // settype(event.target.value);
  //   resetZoom();
  //   setShowCard(false);
  //   setSelectedCity(null);
  // }

  const handleHover = (data, fromMap = false) => {
    setHoveredCity(data !== null ? data.city : null);
    if (data && fromMap && type !== 'topByRegion') {
      const element = document.getElementById(`list-item-${slugify(data.city)}`);
      listRef.current.scrollTo(0, element.offsetTop - 30);
    }
  };

  const handleClick = (data) =>{
    setDetailCard(data);
    setSelectedCity(data.city);
    setShowCard(true);
    popUpRef.current.scrollIntoView({ behavior: "smooth" });
    handleViewStateChange({
      viewState: {
        zoom: 2,
        longitude: data.longitude,
        latitude: data.latitude,
        transitionDuration: 5000,
      },
    });
  }

  const handleShow = (id) => {
    setShowId(showId === id ? -1 : id);
  }

  const cardClose = () =>{
    setShowCard(false);
  }




  return (

    <div className="innerContainer">
        <div className="row flex-md-row-reverse">
          <div className="col-12 col-md col-lg">
            <div className={styles.mapColumn}>
            
                <div className={styles.mapWrapper} ref={mapRef}>
                  <DeckGL
                    viewState={viewport}
                    controller={{scrollZoom:false, doubleClickZoom: false}}
                    onViewStateChange={handleViewStateChange}
                  >
                    <GeoJsonLayer
                      id="map-unselected-countries"
                      data={unselectedCountries ? unselectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[233, 233, 233]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                    />
                    <GeoJsonLayer
                      id="map-selected-countries"
                      data={selectedCountries ? selectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[193, 197, 200]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                      pickable={true}
                    />
                  </DeckGL>
                  <svg
                    className={`${styles.cityMarkers}`}
                    height={"100%"}
                    width={"100%"}
                    viewBox={`0 0 ${width} ${height}`}
                  >
                    {allCitiesData &&
                      allCitiesData.map((d) => {
                        return (
                          <circle
                            className={`${styles.circle}`}
                            key={d.city + "-marker"}
                            cx={getProjectionCoordinates([d.longitude, d.latitude])[0]}
                            cy={getProjectionCoordinates([d.longitude, d.latitude])[1]}
                            r={hoveredCity === d.city ? 10 : 5}
                            fill={hoveredCity === d.city ? "#7DB0DA" : "#405363"}
                            stroke={"#fff"}
                            onTouchStart={() => {
                              handleHover(d, true);
                            }}
                            onMouseEnter={() => {
                              handleHover(d, true);
                            }}
                            onMouseOut={() => {
                              handleHover(null);
                            }}
                            onClick={() => handleClick(d)}
                          >
                            <title>{d.city}</title>
                          </circle>
                        );
                      })}
                  </svg>
                  {allCitiesData &&
                    allCitiesData.map((d) => {
                      return (
                        <React.Fragment key={d.city + "-tooltip"}>
                          {(selectedCity === d.city || hoveredCity === d.city) && (
                            <MapToolTip 
                              name={d.city} 
                              top={getProjectionCoordinates([d.longitude,d.latitude,])[1] - 10}
                              left={getProjectionCoordinates([d.longitude,d.latitude,])[0]}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <MapControls
                      zoomIncrement={() => zoomIncrement(0.5)}
                      zoomDecrement={() => zoomIncrement(-0.5)}
                      resetZoom={() => resetZoom()}
                    /> 
                </div>
            
                <div ref={popUpRef} className={styles.positionToScroll}></div>
                {showCard ? <PopUpDetail item={detailCard} onClose={cardClose} /> : null}
            </div>
            
          </div>
          <div className={`col-12 col-md-4 col-lg-3 ${styles.listColumnMinWidth}`}>
            {type === 'top10' ? (
              <>
                <div className={styles.listWrapper}>
                    <div className={`d-flex align-items-center justify-content-between ${styles.listHeader}`}>
                      {type === 'top10' && (<span className={styles.headerRank}>Rank</span>)}
                        <span className={styles.headerCity}>Wealthiest cities</span>
                        <span>Country</span>
                    </div>
                    <div className={styles.listContainer} ref={listRef}>
                            {allCitiesData &&
                            allCitiesData.map((d, index) => {
                                return (
                                <ListItem
                                    id={`list-item-${slugify(d.city)}`}
                                    key={d.city}
                                    style={{
                                    backgroundColor:
                                        hoveredCity === d.city ? "#C1C5C8" : null,
                                    }}
                                    onMouseOver={() => {
                                    handleHover(d,false);
                                    }}
                                    onMouseOut={() => {
                                    handleHover(null);
                                    }}
                                    onClick={() => handleClick(d)}
                                    data={d}
                                    rank={type === 'top10' ? index + 1 : ''}
                                />
                                );
                            })}                        
                    </div>
                </div>
                <div className={`mt-3 footer-note`}>
                      <ul>
                        <li>All figures are for 31 December 2022.</li>
                        <li>‘High-net-worth individuals’ or ‘HNWIs’ refer to individuals with investable wealth of USD 1 million or more.</li>
                        <li>Includes only HNWIs living in each city (residents).</li>
                        <li>HNWI figures rounded to nearest 100.</li>
                        <li>The Bay Area includes San Francisco and Silicon Valley.</li>
                      </ul>
                      <p>Source: New World Wealth</p>
                </div>
              </>
                ):null
            }
            {type === 'topByRegion' ? (
                <div className={styles.regionWrapper}>
              
                {dataGroup &&
                    dataGroup.map((d,index) => {
                    const listOfCities = d.cities;
                return(
                    <Accordion id={index} show={showId === index} onShow={()=>handleShow(index)} key={d.name}  title={d.name}>
                      
                        {listOfCities.map((item, index)=>{
                        return(
                            <ListItem
                                id={`list-item-${slugify(item.city)}`}
                                key={item.city}
                                style={{
                                backgroundColor:
                                    hoveredCity === item.city ? "#C1C5C8" : null,
                                }}
                                onMouseOver={() => {
                                handleHover(item,false);
                                }}
                                onMouseOut={() => {
                                handleHover(null);
                                }}
                                onClick={() => handleClick(item)}
                                data={item}
                                rank={index + 1}
                            />
                        )
                        })}

                    </Accordion>

                    );
                })}
                  <div className={`mt-3 footer-note`}>
                    <ul>
                      {/* <li>Figures for 2022.</li> */}
                      <li>Figures for 2022.</li>
                      <li>‘High-net-worth individuals’ or ‘HNWIs’ refer to individuals with investable wealth of USD 1 million or more.</li>
                      <li>Includes only HNWIs living in each city (residents).</li>
                      <li>HNWI figures rounded to nearest 100.</li>
                      <li>The Bay Area includes San Francisco and Silicon Valley. For Zurich and Geneva we refer to the canton rather than the city.</li>
                    </ul>
                    <p>Source: New World Wealth</p>
                  </div>
                </div>
            ):null

            }
          </div>
        </div>

    </div>

  );
}

export default MapCities;